// Kid To-do — a dead-simple checklist of the kid's OWN to-dos. Big tap
// targets, one tap to check off, one box to add. No lists/priorities/due
// dates UI (kids don't need that model) — we just use the kid's first list,
// creating a "My to-dos" list on first use. To-dos the kid chat adds (via
// its self-scoped todos tool) show up here too, since both are the kid's
// own user-scoped todos.

import { useEffect, useState } from "preact/hooks";
import { api, type TodoItem } from "../../lib/rpc";

interface KidTodoProps {
  notify: (msg: string) => void;
}

export function KidTodoView({ notify }: KidTodoProps) {
  const [listId, setListId] = useState<string | null>(null);
  const [items, setItems] = useState<TodoItem[] | null>(null);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);

  // Resolve the kid's list (first one, or create "My to-dos"), then load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lists = await api.todoLists();
        const active = lists.filter((l) => !l.archived);
        const list = active[0] || await api.todoCreateList("My to-dos");
        if (cancelled) return;
        setListId(list.id);
        const its = await api.todoListItems(list.id);
        if (!cancelled) setItems(its);
      } catch (e: any) {
        if (!cancelled) { notify(e?.message || "couldn't load to-dos"); setItems([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const add = async () => {
    const title = draft.trim();
    if (!title || !listId || adding) return;
    setAdding(true);
    setDraft("");
    try {
      const item = await api.todoAdd({ listId, title });
      setItems((prev) => prev ? [...prev, item] : [item]);
    } catch (e: any) {
      notify(e?.message || "couldn't add");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (it: TodoItem) => {
    // Optimistic flip.
    setItems((prev) => prev ? prev.map((x) => x.id === it.id ? { ...x, done: !x.done } : x) : prev);
    try {
      await api.todoUpdate(it.id, { done: !it.done });
    } catch (e: any) {
      setItems((prev) => prev ? prev.map((x) => x.id === it.id ? { ...x, done: it.done } : x) : prev);
      notify(e?.message || "couldn't update");
    }
  };

  // Open items first, done items dimmed at the bottom.
  const open = (items || []).filter((i) => !i.done);
  const done = (items || []).filter((i) => i.done);

  return (
    <div className="kid-todo">
      <header className="kid-todo-head">
        <h2 className="kid-todo-title">My to-dos</h2>
      </header>

      <div className="kid-todo-add">
        <input
          className="kid-todo-input"
          placeholder="Add a to-do…"
          value={draft}
          maxLength={200}
          onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <button className="kid-todo-add-btn" type="button" onClick={add} disabled={!draft.trim() || adding} aria-label="Add">＋</button>
      </div>

      {items === null && <div className="kid-empty">loading…</div>}
      {items !== null && open.length === 0 && done.length === 0 && (
        <div className="kid-todo-empty">
          <div className="kid-todo-empty-emoji">✅</div>
          <div>Nothing to do! Add something above.</div>
        </div>
      )}

      {open.map((it) => (
        <button key={it.id} type="button" className="kid-todo-row" onClick={() => toggle(it)}>
          <span className="kid-todo-check" aria-hidden />
          <span className="kid-todo-text">{it.title}</span>
        </button>
      ))}

      {done.length > 0 && (
        <>
          <div className="kid-todo-done-label">Done</div>
          {done.map((it) => (
            <button key={it.id} type="button" className="kid-todo-row is-done" onClick={() => toggle(it)}>
              <span className="kid-todo-check is-checked" aria-hidden>✓</span>
              <span className="kid-todo-text">{it.title}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}
